// ============================================================
// TMR Portal Admin — como a Contingência se lê na tela (31 §9)
// ============================================================
import type { ContingencyRequestStatus, ContingencyType, ContingencyTypeKey } from './contract';

/** What a kind of contingency is called and what it does, for its card and its page. PT-BR product copy. */
export interface ContingencyTypeCopy {
  name: string;
  description: string;
  /** Where its page lives. */
  path: string;
}

/**
 * The copy of each kind, by key. The backend names none of them (it sends keys, never UI text, `07` §5), so a new
 * kind is a new entry here alongside its page.
 */
export const CONTINGENCY_TYPE_COPY: Record<ContingencyTypeKey, ContingencyTypeCopy> = {
  wildcard_code: {
    name: 'Código coringa',
    description:
      'Libera a entrada do paciente no checkout quando o código de verificação não chega ao telefone. Vale por 24 horas em todas as vendas do CPF com link válido, e substitui o coringa anterior do paciente.',
    path: '/contingencia/codigo-coringa',
  },
};

/** @param key A kind's key from the API. */
export function contingencyTypeCopy(key: string): ContingencyTypeCopy {
  return (
    CONTINGENCY_TYPE_COPY[key as ContingencyTypeKey] ?? { name: key, description: '', path: '/contingencia' }
  );
}

/** The list's tabs, in the order a manager works them. */
export const REQUEST_STATUS_OPTIONS: { value: ContingencyRequestStatus; label: string }[] = [
  { value: 'PendingApproval', label: 'Aguardando aprovação' },
  { value: 'Approved', label: 'Aprovados' },
  { value: 'Issued', label: 'Gerados' },
  { value: 'Rejected', label: 'Reprovados' },
];

/**
 * The `StatusBadge` key of each status. Prefixed because `Approved`/`Rejected` already belong to the access
 * requests and the clinic applications, with labels of their own; the keys are registered in `STATUS_MAP`.
 */
export const REQUEST_STATUS_BADGE: Record<ContingencyRequestStatus, string> = {
  PendingApproval: 'contingencia_aguardando',
  Approved: 'contingencia_aprovada',
  Rejected: 'contingencia_reprovada',
  Issued: 'contingencia_gerada',
};

/**
 * Where a kind stands, in the words of its card. The two switches and the environment are different causes, and
 * the card says which one is in the way — "desligado" is a manager's call, "não configurado" is an engineer's.
 *
 * @param type The kind with its setting.
 */
export function typeSituation(type: Pick<ContingencyType, 'enabled' | 'configured' | 'requiresApproval'>): {
  label: string;
  tone: 'success' | 'warning' | 'neutral';
} {
  if (!type.enabled) return { label: 'Desligado', tone: 'neutral' };
  if (!type.configured) return { label: 'Ligado, mas não configurado neste ambiente', tone: 'warning' };
  return { label: type.requiresApproval ? 'Ligado · com aprovação' : 'Ligado · sem aprovação', tone: 'success' };
}

/** @param count Requests waiting for approval. */
export function pendingLabel(count: number): string {
  if (count === 0) return 'Nenhum pedido aguardando aprovação';
  return count === 1 ? '1 pedido aguardando aprovação' : `${count} pedidos aguardando aprovação`;
}

const dateTime = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Sao_Paulo',
});

/**
 * A moment as the panel shows it — day and time in Brasília, because a code's 24 hours are counted to the minute.
 *
 * @param iso An ISO instant from the API.
 */
export function formatMoment(iso: string): string {
  return dateTime.format(new Date(iso)).replace(',', ' às');
}

/**
 * Whether an issued code still works, by its expiry. The panel cannot know whether a newer code voided it; it
 * says only what the clock says.
 *
 * @param codeExpiresAt When the code stops working.
 * @param now The current instant, for tests.
 */
export function codeValidity(codeExpiresAt: string | null, now: Date = new Date()): string | null {
  if (!codeExpiresAt) return null;
  const expires = new Date(codeExpiresAt);
  return expires.getTime() > now.getTime() ? `Válido até ${formatMoment(codeExpiresAt)}` : `Expirou em ${formatMoment(codeExpiresAt)}`;
}
